import clsx from "clsx";
import { AnimatePresence, motion, wrap } from "framer-motion";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React, { DetailedHTMLProps, FC, HTMLAttributes, useCallback, useEffect } from "react";
import { useState } from "react";

import { AnimationVariantsProps } from "./animation-variants-props";
import { SLIDE_TIMER } from "./slide-timer";

// Note: I have chosen to keep the possibility to slide both up and down even if it is currently unused
//  in case we'll choose to give some sort of control to the user about which slide to view
// Note: opacity value is only needed to leave a value for the animation to calculate
// and delay. If there are no such values the element will be terminated
// abruptly, skipping the delay
const variants = {
  enter: ({ mobile, direction }: AnimationVariantsProps) => {
    return mobile
      ? {
          opacity: 1,
          y: direction > 0 ? "100vh" : "-100vh",
          zIndex: 0,
        }
      : {
          opacity: 1,
          y: direction > 0 ? "512px" : "-512px",
          zIndex: 1,
        };
  },
  center: {
    opacity: 1,
    y: 0,
    zIndex: 1,
  },
  exit: {
    opacity: 0,
    y: 0,
    zIndex: 0,
    transition: {
      delay: 0.9,
      zIndex: {
        delay: 0,
      },
    },
  },
};

export interface ImageSliderProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  images: (IGatsbyImageData | null | undefined)[];
  mobile?: boolean;
}

export const ImageSlider: FC<ImageSliderProps> = ({ images, mobile = false, className }) => {
  const [[page, direction], setPage] = useState([0, 0]);

  // We only have 3 images, but we paginate them absolutely (ie 1, 2, 3, 4, 5...) and
  // then wrap that within 0-2 to find our image ID in the array below. By passing an
  // absolute page index as the `motion` component's `key` prop, `AnimatePresence` will
  // detect it as an entirely new image. So you can infinitely paginate as few as 1 images.
  const imageIndex = wrap(0, images.length, page);

  const paginate = useCallback(
    (newDirection: number) => {
      setPage([page + newDirection, newDirection]);
    },
    [page]
  );

  useEffect(() => {
    if (images?.length > 1) {
      const interval = setInterval(() => paginate(1), SLIDE_TIMER);
      return () => clearInterval(interval);
    }
  }, [paginate, images?.length]);

  return (
    <div className={clsx(["h-screen lg:h-128 overflow-hidden", className])}>
      <AnimatePresence initial={false} custom={{ direction, mobile }} mode="popLayout">
        <motion.div
          className="h-full flex w-full flex-col justify-stretch"
          key={page}
          custom={{ direction, mobile }}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            y: { ease: "easeOut", duration: 0.9 },
          }}
        >
          {!!images[imageIndex] && (
            <GatsbyImage
              image={images[imageIndex]!}
              alt=""
              objectFit="cover"
              className="w-full h-full"
            />
          )}
          <div className="h-full w-full absolute z-10 bg-black opacity-40" />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
